import { useTranslation } from "react-i18next"
import { useAuth } from "../contexts/AuthContext"
import Logo from "./Logo"

interface HomePageProps {
  onExplore: () => void
  onLogin: () => void
  onTerms: () => void
}

export default function HomePage({ onExplore, onLogin, onTerms }: HomePageProps) {
  const { t } = useTranslation()
  const { auth } = useAuth()

  return (
    <div className="h-full overflow-y-auto pb-24 bg-gradient-to-b from-slate-50 via-stone-50 to-amber-50/30 dark:from-deepNavy dark:via-deepNavy-light dark:to-deepNavy">
      <div className="relative px-6 pt-10 pb-20 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/autocaravana_background.jpg"
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-deepNavy/70 via-deepNavy/50 to-deepNavy/80" />
        </div>
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none">
            <circle cx="80" cy="80" r="120" fill="white" opacity="0.3" />
            <circle cx="350" cy="200" r="150" fill="white" opacity="0.2" />
            <circle cx="200" cy="380" r="100" fill="white" opacity="0.25" />
          </svg>
        </div>
        <div className="relative z-10 max-w-lg mx-auto text-center">
          <Logo className="w-72 h-72 mx-auto mb-4" />
          <h1 className="text-4xl font-bold tracking-tight mb-3 drop-shadow-lg">{t("home.welcome")}</h1>
          <p className="text-lg opacity-90 mb-8 leading-relaxed max-w-md mx-auto">{t("home.subtitle")}</p>
          <button
            onClick={onExplore}
            className="px-8 py-4 rounded-2xl bg-white/95 text-deepTeal font-bold text-lg shadow-2xl hover:shadow-xl hover:scale-105 active:scale-95 transition-all backdrop-blur-sm"
          >
            {t("home.exploreMap")}
          </button>
        </div>
      </div>

      <div className="px-6 pt-4 pb-10 max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-deepTeal dark:text-steel-light text-center mb-8">{t("home.featuresTitle")}</h2>
        <div className="grid grid-cols-2 gap-4">
          <FeatureCard
            icon="🗺️"
            title={t("home.featureMap")}
            description={t("home.featureMapDesc")}
            gradient="from-steel to-deepTeal"
          />
          <FeatureCard
            icon="📍"
            title={t("home.featureAdd")}
            description={t("home.featureAddDesc")}
            gradient="from-deepTeal to-forest"
          />
          <FeatureCard
            icon="⭐"
            title={t("home.featureRate")}
            description={t("home.featureRateDesc")}
            gradient="from-sand-dark to-amber-600"
          />
          <FeatureCard
            icon="📸"
            title={t("home.featurePhotos")}
            description={t("home.featurePhotosDesc")}
            gradient="from-steel-light to-steel"
          />
        </div>
      </div>

      <div className="px-6 py-10 max-w-lg mx-auto text-center">
        <div className="bg-gradient-to-r from-deepTeal to-forest rounded-2xl p-8 text-white shadow-xl">
          {!auth.isAuthenticated ? (
            <>
              <p className="text-lg mb-6 opacity-95">{t("home.joinCommunity")}</p>
              <button
                onClick={onLogin}
                className="px-8 py-3 rounded-xl bg-white text-deepTeal font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              >
                {t("auth.login")}
              </button>
            </>
          ) : (
            <>
              <p className="text-lg mb-6 opacity-95">{t("home.welcomeBack", { name: auth.user?.username })}</p>
              <button
                onClick={onExplore}
                className="px-8 py-3 rounded-xl bg-white text-deepTeal font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              >
                {t("home.goToMap")}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="px-6 py-8 text-center">
        <button
          onClick={onTerms}
          className="text-sm text-deepTeal dark:text-steel-light hover:underline font-medium"
        >
          {t("terms.title")}
        </button>
        <p className="text-xs text-slate-400 dark:text-slate-600 mt-4"> {new Date().getFullYear()} Autocaravana.site</p>
      </div>
    </div>
  )
}

function FeatureCard({ icon, title, description, gradient }: { icon: string; title: string; description: string; gradient: string }) {
  return (
    <div className="bg-white dark:bg-deepNavy-light rounded-2xl p-5 shadow-md hover:shadow-lg transition-shadow border border-slate-100 dark:border-white/5">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-xl mb-3 shadow-sm`}>
        {icon}
      </div>
      <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm mb-1">{title}</h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{description}</p>
    </div>
  )
}

